import React from 'react';
import { motion } from 'framer-motion';
import { FiPlay, FiCheckCircle, FiArrowRight, FiSettings, FiShield, FiBarChart, FiGitBranch, FiUsers, FiClock, FiTrendingUp, FiBook, FiHelpCircle } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MobileFeatureCarousel from '../components/MobileFeatureCarousel';
import ImageModal from '../components/ImageModal';

const GettingStarted = () => {
  const [imageModal, setImageModal] = React.useState<{
    isOpen: boolean;
    src: string;
    alt: string;
    title?: string;
  }>({
    isOpen: false,
    src: '',
    alt: '',
    title: ''
  });

  const openImageModal = (src: string, alt: string, title?: string) => {
    setImageModal({ isOpen: true, src, alt, title });
  };

  const closeImageModal = () => {
    setImageModal({ isOpen: false, src: '', alt: '', title: '' });
  };

  const quickStartSteps = [
    {
      title: "Connect Your Repository",
      description: "Link your GitHub, Bitbucket, or GitLab account to automatically discover and import your projects for security analysis.",
      icon: <FiGitBranch className="w-8 h-8" />,
      details: [
        "Navigate to Integrations page from the sidebar",
        "Click 'Connect' for your preferred Git provider",
        "Authorize Ana Security to access your repositories",
        "Select repositories you want to analyze"
      ]
    },
    {
      title: "Run Your First Scan",
      description: "Execute a comprehensive security scan on your project to identify vulnerabilities and establish a security baseline.",
      icon: <FiShield className="w-8 h-8" />,
      details: [
        "Go to Projects page and select your repository",
        "Click the orange 'New Scan' button",
        "Choose scan types (SCA, SAST, Secrets Detection)",
        "Wait for scan completion and review results"
      ]
    },
    {
      title: "Review Security Findings",
      description: "Analyze the security scan results, understand vulnerability severity levels, and prioritize remediation efforts.",
      icon: <FiBarChart className="w-8 h-8" />,
      details: [
        "Access the Dashboard to see overall security metrics",
        "Review findings by severity (Critical, High, Medium, Low)",
        "Click on individual issues for detailed information",
        "Use filtering options to focus on specific vulnerability types"
      ]
    },
    {
      title: "Fix Issues with ANA Actions",
      description: "Leverage automated fix suggestions and pull request generation to quickly resolve security vulnerabilities.",
      icon: <FiCheckCircle className="w-8 h-8" />,
      details: [
        "Look for orange 'Fix' buttons next to vulnerabilities",
        "Click 'Fix' to generate automated remediation",
        "Review the proposed changes in the pull request",
        "Merge the PR to apply security fixes to your codebase"
      ]
    }
  ];

  const securityModules = [
    {
      title: "SCA - Software Composition Analysis",
      description: "Scan dependencies for known vulnerabilities and license compliance issues with automated fix suggestions.",
      icon: <FiShield className="w-8 h-8" />,
      details: [
        "Identifies vulnerable dependencies in your project",
        "Provides automated fix suggestions via ANA Actions",
        "Tracks license compliance and policy violations",
        "Monitors for new vulnerabilities in existing dependencies"
      ]
    },
    {
      title: "SAST - Static Application Security Testing",
      description: "Analyze source code for security vulnerabilities without executing the application.",
      icon: <FiBarChart className="w-8 h-8" />,
      details: [
        "Scans source code for common security patterns",
        "Identifies potential injection vulnerabilities",
        "Detects insecure coding practices and patterns",
        "Provides detailed remediation guidance"
      ]
    },
    {
      title: "Secrets Detection",
      description: "Prevent credential leaks by scanning for exposed API keys, passwords, and sensitive information.",
      icon: <FiUsers className="w-8 h-8" />,
      details: [
        "Scans code repositories for exposed credentials",
        "Detects API keys, passwords, and tokens",
        "Monitors commit history for leaked secrets",
        "Provides guidance for secure credential management"
      ]
    },
    {
      title: "SBOM - Software Bill of Materials",
      description: "Generate comprehensive inventories of software components for supply chain security and compliance.",
      icon: <FiSettings className="w-8 h-8" />,
      details: [
        "Creates detailed software component inventories",
        "Tracks direct and transitive dependencies",
        "Supports SPDX and CycloneDX standards",
        "Enables supply chain risk assessment"
      ]
    }
  ];

  const bestPractices = [
    {
      title: "Start with SCA Scanning",
      description: "Begin your security journey with Software Composition Analysis for quick wins and immediate value.",
      icon: <FiPlay className="w-8 h-8" />,
      details: [
        "SCA often finds the most actionable vulnerabilities",
        "Automated fixes are available for many dependency issues",
        "Quick to run and provides immediate security improvements",
        "Establishes a foundation for comprehensive security practices"
      ]
    },
    {
      title: "Prioritize by Severity",
      description: "Focus on Critical and High severity vulnerabilities first for maximum security impact.",
      icon: <FiTrendingUp className="w-8 h-8" />,
      details: [
        "Critical: Fix immediately - high risk of exploitation",
        "High: Fix this week - significant security risk",
        "Medium: Fix when convenient - moderate risk",
        "Low: Fix during maintenance - minimal immediate risk"
      ]
    },
    {
      title: "Integrate into CI/CD",
      description: "Set up automated security scanning in your development workflow for continuous protection.",
      icon: <FiSettings className="w-8 h-8" />,
      details: [
        "Configure GitHub Actions or other CI/CD integrations",
        "Set up automated scanning on pull requests",
        "Establish security gates for deployment pipelines",
        "Monitor security trends over time"
      ]
    },
    {
      title: "Regular Security Reviews",
      description: "Establish routine security practices for ongoing protection and continuous improvement.",
      icon: <FiClock className="w-8 h-8" />,
      details: [
        "Daily: Check dashboard for new findings",
        "Weekly: Review and address high-priority issues",
        "Monthly: Analyze security trends and update policies",
        "Quarterly: Comprehensive security assessment and planning"
      ]
    }
  ];

  const commonWorkflows = [
    {
      workflow: "New Project Setup",
      description: "Complete workflow for setting up security scanning on a new project",
      steps: [
        "Connect repository via Integrations page",
        "Navigate to Projects and locate your new project",
        "Run initial comprehensive scan (SCA + SAST + Secrets)",
        "Review findings and establish security baseline",
        "Set up automated scanning schedule"
      ]
    },
    {
      workflow: "Vulnerability Remediation",
      description: "Step-by-step process for addressing security vulnerabilities",
      steps: [
        "Review Dashboard for new security findings",
        "Prioritize issues by severity and exploitability",
        "Use ANA Actions for automated fixes where available",
        "Manually address remaining vulnerabilities",
        "Re-scan to verify fixes and track progress"
      ]
    },
    {
      workflow: "Compliance Reporting",
      description: "Generate security reports for compliance and audit purposes",
      steps: [
        "Access SBOM module for software inventory",
        "Generate compliance reports from Dashboard",
        "Export vulnerability data and remediation status",
        "Document security improvements and trends",
        "Share reports with stakeholders and auditors"
      ]
    }
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-16"
      >
        <ScrollHeader title="Getting Started with Ana Security" />
        
        {/* Hero Section */}
        <div className="text-center">
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-4xl mx-auto leading-relaxed">
            Welcome to Ana Security! This comprehensive guide will help you get started with security scanning and vulnerability management. 
            Follow our streamlined process to secure your applications and establish robust security workflows.
          </p>
          
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border border-orange-200 dark:border-orange-700/50 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-orange-500 text-white rounded-full p-4">
                <FiPlay className="w-8 h-8" />
              </div>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              🚀 Quick Start: 4 Steps to Security
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg">
              Get your first security scan running in under 10 minutes with our streamlined onboarding process.
            </p>
          </div>
        </div>

        {/* Quick Start Steps */}
        <div>
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Quick Start Guide
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Follow these four simple steps to get your security scanning up and running
            </p>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-8">
            {quickStartSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start gap-8 bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
              >
                <div className="w-16 h-16 bg-orange-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold flex-shrink-0 shadow-lg">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6 text-lg leading-relaxed">
                    {step.description}
                  </p>
                  <ul className="space-y-3">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <FiArrowRight className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={quickStartSteps.map((step, index) => ({
                title: `Step ${index + 1}: ${step.title}`,
                description: step.description,
                icon: step.icon,
                details: step.details
              }))}
              autoPlay={true}
              autoPlayInterval={8000}
            />
          </div>
        </div>

        {/* Security Modules Overview */}
        <div>
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Security Modules Overview
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Ana Security provides comprehensive security scanning across multiple modules. Start with SCA for quick wins, then expand to other modules based on your needs.
            </p>
          </div>
          
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-8">
            {securityModules.map((module, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-orange-500 bg-orange-100 dark:bg-orange-900/30 p-3 rounded-xl">
                    {module.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {module.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                  {module.description}
                </p>
                <ul className="space-y-3">
                  {module.details?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={securityModules}
              autoPlay={true}
              autoPlayInterval={7000}
            />
          </div>
        </div>

        {/* Best Practices */}
        <div>
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Security Best Practices
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Follow these proven practices to maximize your security posture
            </p>
          </div>
          
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-8">
            {bestPractices.map((practice, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="text-orange-500 bg-orange-100 dark:bg-orange-900/30 p-3 rounded-xl flex-shrink-0">
                    {practice.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {practice.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {practice.description}
                    </p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {practice.details?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={bestPractices}
              autoPlay={true}
              autoPlayInterval={6000}
            />
          </div>
        </div>

        {/* Common Workflows */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Common Workflows
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Follow these proven workflows for common security tasks and scenarios
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {commonWorkflows.map((workflow, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
              >
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {workflow.workflow}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                  {workflow.description}
                </p>
                <ol className="space-y-3">
                  {workflow.steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        {idx + 1}
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="text-center bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-3xl p-8 md:p-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Start Your Security Journey?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
            Take the first step towards comprehensive application security with Ana Security.
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <motion.a 
              href="/integrations" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-4 p-6 bg-orange-500 text-white rounded-2xl hover:bg-orange-600 transition-colors shadow-lg"
            >
              <FiGitBranch className="w-8 h-8 flex-shrink-0" />
              <div className="text-left">
                <h3 className="font-bold text-lg">Connect Repository</h3>
                <p className="text-sm opacity-90">Start by linking your Git provider</p>
              </div>
            </motion.a>
            
            <motion.a 
              href="/projects" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-4 p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl hover:border-orange-500/30 transition-colors shadow-lg"
            >
              <FiShield className="w-8 h-8 text-orange-500 flex-shrink-0" />
              <div className="text-left">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white">Run First Scan</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Execute your initial security assessment</p>
              </div>
            </motion.a>
            
            <motion.a 
              href="/dashboard" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-4 p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl hover:border-orange-500/30 transition-colors shadow-lg sm:col-span-2 lg:col-span-1"
            >
              <FiBarChart className="w-8 h-8 text-orange-500 flex-shrink-0" />
              <div className="text-left">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white">View Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monitor your security metrics</p>
              </div>
            </motion.a>
          </div>
        </div>

        {/* Help and Support */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-xl">
              <FiHelpCircle className="w-8 h-8 text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Need Help Getting Started?
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              <FiBook className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Quick Start Guide</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Follow the 4-step process above for fastest setup</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <FiBook className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">User Guide</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Comprehensive documentation for all features</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <FiCheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Best Practices</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Learn from security experts and industry standards</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Image Modal */}
      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={closeImageModal}
        src={imageModal.src}
        alt={imageModal.alt}
        title={imageModal.title}
      />
    </div>
  );
};

export default GettingStarted;
